import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BodyL, BodyS } from '@sorare/core/src/atoms/typography';
import { Card3DWithFallback } from '@sorare/core/src/components/3d/Card3DWithFallback';
import { glossary } from '@sorare/core/src/lib/glossary';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';
import { toCamelCase } from '@sorare/core/src/lib/toCamelCase';
import { theme } from '@sorare/core/src/style/theme';

import { AddTeamLayout } from 'components/league/AddTeamLayout';
import { useFootballEvents } from 'lib/events';

import descriptionBg from '../YourNextStep/assets/description-bg.svg';
import { CompetitionUnlockedWithCard_so5League } from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  flex: 1;
  margin-bottom: calc(8 * var(--unit));
`;

const GradientText = styled.span`
  background: var(--gradient-bg);
  background-size: 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;
`;

const Description = styled.div`
  background-image: url(${descriptionBg});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  padding: var(--double-unit) 0;
`;

const CardWrapper = styled.div`
  width: 30%;
  aspect-ratio: var(--card-aspect-ratio);
`;

const CTA = styled(LinkOverlay)`
  &,
  &:hover {
    color: var(--c-black);
  }
`;

type Props = {
  so5League: CompetitionUnlockedWithCard_so5League;
  to: Nullable<string>;
};

export const CompetitionUnlockedWithCard = ({ so5League, to }: Props) => {
  const track = useFootballEvents();
  const firstMissingRarity = Rarity.limited;

  const so5Leaderboard = so5League.so5LeagueTracks.at(0)?.entrySo5Leaderboard;
  return (
    <AddTeamLayout so5LeaderboardGroup={so5League} lineupStyle="spread">
      <Wrapper center gap={4}>
        <Vertical center>
          <CardWrapper>
            {so5League.unlockedWithCard && (
              <Card3DWithFallback
                card={so5League.unlockedWithCard}
                forceDisable3d
              />
            )}
          </CardWrapper>
          <BodyL bold>
            <FormattedMessage
              id="CompetitionUnlockedWithCard.title"
              defaultMessage="Your New Card unlocked a Competition"
            />
          </BodyL>
          {to && (
            <CTA
              as={Button}
              color="secondary"
              size="medium"
              to={to}
              onClick={() => {
                track('Enter Add Team From Lobby', {
                  source: 'Competition Unlocked By Card',
                });
              }}
            >
              <FormattedMessage {...glossary.playNow} />
            </CTA>
          )}
        </Vertical>
        <Description>
          <BodyS bold style={{ zIndex: 1 }}>
            <FormattedMessage
              id="CompetitionUnlockedWithCard.description2"
              defaultMessage="Compete with <span>{count, plural, one {# {rarity} Card} other {# {rarity} Cards}}</span>"
              values={{
                span: chunks => (
                  <GradientText
                    style={
                      {
                        '--gradient-bg':
                          theme.colors.scarcity[
                            toCamelCase(firstMissingRarity)
                          ],
                      } as CSSProperties
                    }
                  >
                    {chunks}
                  </GradientText>
                ),
                count: so5Leaderboard?.rules?.rarityLimits?.limited.max || 1,
                rarity: (
                  <FormattedMessage {...scarcityMessages[firstMissingRarity]} />
                ),
              }}
            />
          </BodyS>
        </Description>
      </Wrapper>
    </AddTeamLayout>
  );
};

CompetitionUnlockedWithCard.fragments = {
  so5League: gql`
    fragment CompetitionUnlockedWithCard_so5League on So5League {
      slug
      unlockedWithCard {
        ... on AnyCardInterface {
          slug
          ...Card3DWithFallback_anyCardInterface
        }
      }
      so5LeagueTracks {
        slug
        maxManagerTeamsCount
        entrySo5Leaderboard {
          slug
          rules {
            id
            rarityLimits {
              limited {
                min
                max
              }
            }
          }
        }
      }
      ...AddTeamLayout_so5LeaderboardGroupInterface
    }
    ${AddTeamLayout.fragments.so5LeaderboardGroupInterface}
    ${Card3DWithFallback.fragments.anyCardInterface}
  ` as TypedDocumentNode<CompetitionUnlockedWithCard_so5League>,
};
